import { useCallback, useEffect, useRef } from 'react';
import { fieldMappingService } from '~/src/entities/fieldMapping/services';
import type { FieldMappingOverrideSubjectType } from '~/src/entities/fieldMapping/services/fieldMappingService';
import { useCurrentOrgFprint } from '~/src/entities/user';

const TEXT_OVERRIDE_DEBOUNCE_MS = 400;

export type FieldMappingOverrideSubject = {
  fieldMappingSubjectType: FieldMappingOverrideSubjectType;
  fieldMappingSubjectId: number;
};

type UseFieldMappingOverrideArgs = {
  hasActiveMapping?: boolean;
  fieldMappingOverrideSubject?: FieldMappingOverrideSubject;
  fieldMappingId?: number;
  fieldMappingSourceName?: string;
};

export const useFieldMappingOverride = ({
  hasActiveMapping,
  fieldMappingOverrideSubject,
  fieldMappingId,
  fieldMappingSourceName,
}: UseFieldMappingOverrideArgs) => {
  const orgFprint = useCurrentOrgFprint();

  const shouldCallOverrideApi =
    !!hasActiveMapping &&
    !!orgFprint &&
    !!fieldMappingOverrideSubject &&
    !!fieldMappingId;

  const textOverrideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const overriddenSourceNameRef = useRef<string | null>(null);

  const triggerOverride = useCallback(() => {
    if (!shouldCallOverrideApi) return;

    // If a mapping has already been marked as overriden for one change, don't need to mark it again.
    if (overriddenSourceNameRef.current === fieldMappingSourceName) return;

    fieldMappingService
      .setFieldMappingOverride({
        orgFprint,
        fieldMappingSubjectType:
          fieldMappingOverrideSubject.fieldMappingSubjectType,
        fieldMappingSubjectId:
          fieldMappingOverrideSubject.fieldMappingSubjectId,
        fieldMappingId,
        isOverride: true,
      })
      .then(() => {
        overriddenSourceNameRef.current = fieldMappingSourceName ?? null;
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to set field mapping override flag', e);
      });
  }, [
    shouldCallOverrideApi,
    orgFprint,
    fieldMappingOverrideSubject,
    fieldMappingId,
    fieldMappingSourceName,
  ]);

  const triggerTextOverrideDebounced = useCallback(() => {
    if (!shouldCallOverrideApi) return;

    if (overriddenSourceNameRef.current === fieldMappingSourceName) return;

    if (textOverrideTimeoutRef.current) {
      clearTimeout(textOverrideTimeoutRef.current);
    }

    textOverrideTimeoutRef.current = setTimeout(() => {
      triggerOverride();
    }, TEXT_OVERRIDE_DEBOUNCE_MS);
  }, [shouldCallOverrideApi, fieldMappingSourceName, triggerOverride]);

  useEffect(() => {
    return () => {
      if (textOverrideTimeoutRef.current) {
        clearTimeout(textOverrideTimeoutRef.current);
        textOverrideTimeoutRef.current = null;
      }
    };
  }, [fieldMappingSourceName, triggerOverride]);

  return {
    triggerOverride,
    triggerTextOverrideDebounced,
  };
};
